/**
 * Shared WalletConnect v2 plumbing for mobile-wallet sign-in flows
 * (Jupiter currently; Phantom doesn't ship a WC handler in their
 * mobile app and uses the native deeplink protocol instead).
 *
 * One cached SignClient is shared across callers — initialising it
 * opens a WebSocket to the WC relay and pulls in ~133KB gzipped, so
 * we keep it alive for the lifetime of the page.
 *
 * Sign-in flow (`connectViaWalletConnect`): connect + signMessage in
 * one round-trip. We deliberately do NOT disconnect afterward so the
 * same session can sign transactions later via `signTransactionViaWC`
 * — collapsing the in-app credit/swap purchase UX from "re-pair"
 * to "tap, approve, done".
 */
import bs58 from 'bs58';

const SOLANA_MAINNET = 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp';

const CONNECT_TIMEOUT_MS = 90_000;
const SIGN_TIMEOUT_MS = 60_000;

// Persisted across page reloads so transaction-signing flows can
// detect "we have a live WC session" without re-initialising the
// SignClient first. The actual session state lives in WC's own
// internal storage; these are just our quick-lookup pointers.
const LSK_TOPIC       = 'wc_session_topic';
const LSK_WALLET_ADDR = 'wc_session_wallet_address';
const LSK_OPEN_URI    = 'wc_session_open_uri'; // URI to bring the wallet app to front

let cachedClient: any = null;
let visibilityListenerInstalled = false;

async function getClient(): Promise<any> {
  if (cachedClient) return cachedClient;
  const { SignClient } = await import('@walletconnect/sign-client');
  const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
  if (!projectId) {
    throw new Error('WalletConnect sign-in is not configured (missing REACT_APP_WALLETCONNECT_PROJECT_ID)');
  }
  cachedClient = await SignClient.init({
    projectId,
    metadata: {
      name: 'deepnets.ai',
      description: 'Solana token intelligence',
      url: window.location.origin,
      icons: [`${window.location.origin}/logo192.png`],
      // Wallets that honor `metadata.redirect.universal` will auto-
      // return the user to Safari after they approve. Best-effort —
      // the field is optional in the WC spec, and Jupiter Mobile
      // silently ignores it.
      redirect: {
        universal: window.location.origin,
      },
    },
  });

  // On iOS, switching to the wallet app suspends Safari and kills the
  // WC relay's WebSocket. The client *will* reconnect on its own once
  // we foreground, but it can take 30+ seconds. Kicking transportOpen/
  // restartTransport on visibilitychange shaves that down considerably.
  if (typeof document !== 'undefined' && !visibilityListenerInstalled) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const relayer = cachedClient?.core?.relayer;
        relayer?.transportOpen?.().catch(() => {});
        relayer?.restartTransport?.().catch(() => {});
      } catch { /* best-effort */ }
    });
    visibilityListenerInstalled = true;
  }

  return cachedClient;
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms);
    p.then(
      v => { clearTimeout(t); resolve(v); },
      e => { clearTimeout(t); reject(e); },
    );
  });
}

export interface WalletConnectSignResult {
  publicKey: string;
  signature: Uint8Array;
}

export interface WalletConnectMobileOpts {
  /** Human-readable wallet name, used in error messages. */
  walletLabel: string;
  /**
   * Build the deep link used to hand the WC URI to the wallet. Most
   * wallets accept either a universal link (`https://wallet.com/wc?uri=…`)
   * or a custom scheme (`wallet://wc?uri=…`). Prefer the universal
   * link when the wallet publishes one — opens the App Store fallback
   * if the wallet isn't installed instead of silently no-op'ing.
   */
  buildWalletUri: (wcUri: string) => string;
  /**
   * Bring the wallet to the foreground without handing it a new URI.
   * Used after the session is established, when we want the user to
   * see a follow-up request (transaction sign). Most wallets accept
   * their bare custom scheme (`jupiter://`, etc.).
   */
  walletOpenUri: string;
  /** Fired once the WC URI has been opened so the UI can prompt the
   *  user to switch to the wallet app. */
  onWaiting?: () => void;
}

/**
 * Initiate a WalletConnect connect + signMessage round-trip with the
 * given wallet. Returns the wallet's public key and the signature
 * over `message`. Persists the session topic so `signTransactionViaWC`
 * can reuse the same connection without re-pairing.
 */
export async function connectViaWalletConnect(
  message: Uint8Array,
  opts: WalletConnectMobileOpts,
): Promise<WalletConnectSignResult> {
  const client = await getClient();

  // Always start a fresh connection. Reusing sessions would skip the
  // "approve" dialog but our login message includes a fresh timestamp
  // every time, so the user has to sign again anyway.
  const { uri, approval } = await client.connect({
    requiredNamespaces: {
      solana: {
        chains: [SOLANA_MAINNET],
        methods: ['solana_signMessage', 'solana_signTransaction'],
        events: [],
      },
    },
  });

  if (!uri) throw new Error('WalletConnect did not return a connection URI');

  // Hand the WC URI to the wallet. iOS will switch to the wallet app
  // if installed; if not, we time out below with a clear error.
  window.location.href = opts.buildWalletUri(uri);

  opts.onWaiting?.();

  const session: any = await withTimeout(
    approval(),
    CONNECT_TIMEOUT_MS,
    `${opts.walletLabel} connection`,
  );

  const accounts: string[] = session?.namespaces?.solana?.accounts ?? [];
  if (accounts.length === 0) throw new Error(`${opts.walletLabel} session has no Solana accounts`);
  const publicKey = accounts[0].split(':').pop()!;

  const messageBase58 = bs58.encode(message);
  const result: { signature: string } = await withTimeout(
    client.request({
      topic: session.topic,
      chainId: SOLANA_MAINNET,
      request: {
        method: 'solana_signMessage',
        params: { message: messageBase58, pubkey: publicKey },
      },
    }),
    SIGN_TIMEOUT_MS,
    `${opts.walletLabel} signMessage`,
  );

  if (!result?.signature) throw new Error(`${opts.walletLabel} did not return a signature`);
  const signature = bs58.decode(result.signature);

  // Persist the session for transaction-signing reuse.
  // We deliberately keep the WC session alive (no client.disconnect)
  // so subsequent solana_signTransaction calls can ride the same
  // pairing — saves the user from re-pairing every purchase.
  localStorage.setItem(LSK_TOPIC, session.topic);
  localStorage.setItem(LSK_WALLET_ADDR, publicKey);
  localStorage.setItem(LSK_OPEN_URI, opts.walletOpenUri);

  return { publicKey, signature };
}

/**
 * Returns true if we have a persisted WC session for the given wallet
 * AND the SignClient still has it in its session store. Falsey state
 * is silently cleared so the caller can fall back to a fresh connect
 * without dragging stale flags around.
 */
export async function hasLiveWCSession(walletAddress: string | null): Promise<boolean> {
  const topic = localStorage.getItem(LSK_TOPIC);
  const savedWallet = localStorage.getItem(LSK_WALLET_ADDR);
  if (!topic || !savedWallet) return false;
  if (walletAddress && savedWallet !== walletAddress) {
    // Different account signed in elsewhere — drop our stale view.
    clearWCSession();
    return false;
  }

  try {
    const client = await getClient();
    const session = client.session.get(topic);
    if (!session) {
      clearWCSession();
      return false;
    }
    // expiry is unix-seconds; some SDK versions use ms.
    const expiry = session.expiry < 10_000_000_000 ? session.expiry * 1000 : session.expiry;
    if (expiry && Date.now() >= expiry) {
      clearWCSession();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Synchronous best-effort check used by render paths that can't await. */
export function hasLiveWCSessionSync(walletAddress: string | null): boolean {
  const topic = localStorage.getItem(LSK_TOPIC);
  const savedWallet = localStorage.getItem(LSK_WALLET_ADDR);
  if (!topic || !savedWallet) return false;
  if (walletAddress && savedWallet !== walletAddress) return false;
  return true;
}

/** Wipe our local pointers and tell the SignClient (best-effort) that
 *  we're done with the session. */
export async function clearWCSession(): Promise<void> {
  const topic = localStorage.getItem(LSK_TOPIC);
  for (const k of [LSK_TOPIC, LSK_WALLET_ADDR, LSK_OPEN_URI]) {
    localStorage.removeItem(k);
  }
  if (!topic) return;
  try {
    const client = cachedClient || (await getClient());
    await client.disconnect({ topic, reason: { code: 6000, message: 'User logged out' } });
  } catch { /* relay will GC */ }
}

/**
 * Try-decode a wallet response string as base58, then base64. The
 * Reown spec for `solana_signTransaction` says base58, but Jupiter
 * (and some others) return base64. Detection is structural — if
 * base58 decode throws on a non-base58 character, we assume base64.
 */
function decodeWalletReply(str: string): Uint8Array {
  try {
    return bs58.decode(str);
  } catch {
    // Fall through to base64. atob throws on invalid base64.
    try {
      return Uint8Array.from(atob(str), c => c.charCodeAt(0));
    } catch {
      throw new Error('Wallet returned a signed tx in an unknown encoding (not base58 or base64)');
    }
  }
}

/**
 * Sign a Solana transaction using the saved WC session. The wallet
 * needs to be brought to the foreground for the user to approve, so
 * we navigate to its native scheme right before publishing the
 * `solana_signTransaction` request — the relay routes it; the user
 * sees the approval dialog when they look at the wallet.
 *
 * Returns the raw bytes of the fully-signed transaction. Callers
 * can re-encode for whatever their submit endpoint expects.
 */
export async function signTransactionViaWC(transactionBase58: string): Promise<Uint8Array> {
  const topic = localStorage.getItem(LSK_TOPIC);
  const walletAddress = localStorage.getItem(LSK_WALLET_ADDR);
  const openUri = localStorage.getItem(LSK_OPEN_URI);
  if (!topic || !walletAddress) {
    throw new Error('No WalletConnect session — sign in first');
  }

  const client = await getClient();
  if (!client.session.get(topic)) {
    clearWCSession();
    throw new Error('WalletConnect session expired — please sign in again');
  }

  // Bring the wallet app to the foreground so the user can approve.
  // Then wait a beat before publishing the request: on iOS the wallet
  // is typically backgrounded between sign requests in the same
  // session, and its WC relay WebSocket is suspended along with it.
  // If we publish while the wallet's WS is still cold-reconnecting,
  // the relay queues the request but the wallet doesn't have the
  // socket to deliver it through — user sees a system notification
  // but no in-wallet approval UI. A short delay gives the wallet
  // time to come to foreground, re-open its WS, and re-subscribe to
  // our session topic before the request lands.
  if (openUri) {
    window.location.href = openUri;
    await new Promise(r => setTimeout(r, 1500));
  }

  // Per Reown's Solana namespace spec, solana_signTransaction takes
  // ONLY `{ transaction: <base58> }` — passing extra fields (like
  // `pubkey`) causes some wallets to silently reject the request,
  // showing a system notification but never surfacing an approval
  // dialog. Strict params here.
  const result: { transaction?: string; signature?: string } = await withTimeout(
    client.request({
      topic,
      chainId: SOLANA_MAINNET,
      request: {
        method: 'solana_signTransaction',
        params: { transaction: transactionBase58 },
      },
    }),
    SIGN_TIMEOUT_MS,
    'WalletConnect signTransaction',
  );

  // Spec says { transaction: <signed tx string> }. Jupiter-via-Reown
  // returns the same field but the encoding varies — decode flexibly.
  // Some signature-only wallets return { signature } instead, but
  // that's rarer for solana_signTransaction; we throw clearly if so.
  const signed = result?.transaction;
  if (!signed) {
    if (result?.signature) {
      throw new Error('Wallet returned only a signature, not a signed transaction — unsupported flow');
    }
    throw new Error('Wallet did not return a signed transaction');
  }
  return decodeWalletReply(signed);
}
